import { createHeaders, useServerState } from "@project-mgmt/api-client";
import { mutate } from "swr";
/**
 * Hook to fetch projects list with filtering
 */
export function useProjects(workspaceId, filters) {
    const queryParams = new URLSearchParams();
    // Add workspace filter
    if (workspaceId)
        queryParams.set("workspaceId", workspaceId);
    if (filters?.status)
        queryParams.set("status", filters.status);
    if (filters?.assignedTo)
        queryParams.set("assignedTo", filters.assignedTo);
    if (filters?.limit)
        queryParams.set("limit", filters.limit.toString());
    if (filters?.offset)
        queryParams.set("offset", filters.offset.toString());
    const queryString = queryParams.toString();
    const url = `/api/projects${queryString ? `?${queryString}` : ""}`;
    return useServerState({
        key: ["projects", workspaceId, filters],
        fetcher: async (signal) => {
            const response = await fetch(url, {
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to fetch projects");
            }
            return response.json();
        },
        onSuccess: (data) => {
            // Optional success callback
        },
        onError: (error) => {
            console.error("Failed to fetch projects:", error);
        },
    });
}
/**
 * Hook to fetch a single project by ID
 */
export function useProject(projectId, workspaceId) {
    return useServerState({
        key: ["project", projectId, workspaceId],
        fetcher: async (signal) => {
            const response = await fetch(`/api/projects/${projectId}?workspaceId=${workspaceId}`, {
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to fetch project");
            }
            return response.json();
        },
        onError: (error) => {
            console.error("Failed to fetch project:", error);
        },
    });
}
/**
 * Hook to create a new project
 */
export function useCreateProject() {
    return useServerState({
        key: ["projects:create"],
        fetcher: async (data, signal) => {
            const { workspaceId, ...projectData } = data;
            const response = await fetch(`/api/projects?workspaceId=${workspaceId}`, {
                method: "POST",
                headers: createHeaders(workspaceId),
                body: JSON.stringify(projectData),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to create project");
            }
            return response.json();
        },
        manual: true,
        onSuccess: (project) => {
            // Invalidate all projects list caches for this workspace
            if (project && "workspaceId" in project) {
                mutate((key) => {
                    if (typeof key === "string") {
                        try {
                            const parsed = JSON.parse(key);
                            return Array.isArray(parsed) && parsed[0] === "projects" && parsed[1] === project.workspaceId;
                        }
                        catch {
                            return false;
                        }
                    }
                    return false;
                }, undefined, { revalidate: true });
            }
        },
        onError: (error) => {
            console.error("Failed to create project:", error);
        },
    });
}
/**
 * Hook to update a project
 */
export function useUpdateProject(projectId, workspaceId) {
    return useServerState({
        key: ["projects:update", projectId, workspaceId],
        fetcher: async (data, signal) => {
            const response = await fetch(`/api/projects/${projectId}?workspaceId=${workspaceId}`, {
                method: "PUT",
                headers: createHeaders(workspaceId),
                body: JSON.stringify(data),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to update project");
            }
            return response.json();
        },
        manual: true,
        onSuccess: () => {
            // Invalidate all projects list caches for this workspace
            mutate((key) => {
                if (typeof key === "string") {
                    try {
                        const parsed = JSON.parse(key);
                        return Array.isArray(parsed) && parsed[0] === "projects" && parsed[1] === workspaceId;
                    }
                    catch {
                        return false;
                    }
                }
                return false;
            }, undefined, { revalidate: true });
            // Also invalidate the specific project cache
            const projectKey = JSON.stringify(["project", projectId, workspaceId]);
            mutate(projectKey, undefined, { revalidate: true });
        },
        onError: (error) => {
            console.error("Failed to update project:", error);
        },
    });
}
/**
 * Hook to archive a project
 */
export function useArchiveProject(workspaceId) {
    return useServerState({
        key: ["projects:archive", workspaceId],
        fetcher: async (projectId, signal) => {
            if (!projectId) {
                throw new Error("Project ID is required");
            }
            console.log("Archiving project:", projectId, "in workspace:", workspaceId);
            const response = await fetch(`/api/projects/${projectId}?workspaceId=${workspaceId}`, {
                method: "DELETE",
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to archive project");
            }
            return response.json();
        },
        manual: true,
        onSuccess: () => {
            // Invalidate all projects list caches for this workspace
            const projectsKey = JSON.stringify(["projects", workspaceId]);
            mutate((key) => {
                if (typeof key === "string") {
                    try {
                        const parsed = JSON.parse(key);
                        return Array.isArray(parsed) && parsed[0] === "projects" && parsed[1] === workspaceId;
                    }
                    catch {
                        return false;
                    }
                }
                return false;
            }, undefined, { revalidate: true });
        },
        onError: (error) => {
            console.error("Failed to archive project:", error);
        },
    });
}
/**
 * Hook to assign a user to a project
 */
export function useAssignUserToProject(projectId, workspaceId) {
    return useServerState({
        key: ["projects:assign", projectId, workspaceId],
        fetcher: async (data, signal) => {
            const response = await fetch(`/api/projects/${projectId}/assignments?workspaceId=${workspaceId}`, {
                method: "POST",
                headers: createHeaders(workspaceId),
                body: JSON.stringify(data),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to assign user to project");
            }
            return response.json();
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to assign user to project:", error);
        },
    });
}
/**
 * Hook to remove a user assignment from a project
 */
export function useRemoveUserFromProject(projectId, userId, workspaceId) {
    return useServerState({
        key: ["projects:unassign", projectId, userId, workspaceId],
        fetcher: async (role, signal) => {
            const queryParams = new URLSearchParams({ workspaceId });
            if (role)
                queryParams.set("role", role);
            const url = `/api/projects/${projectId}/assignments/${userId}?${queryParams.toString()}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: createHeaders(workspaceId),
                signal,
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData?.error || "Failed to remove user from project");
            }
            return response.json();
        },
        manual: true,
        onError: (error) => {
            console.error("Failed to remove user from project:", error);
        },
    });
}
